import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { DialogProps, useDialogs } from '@toolpad/core/useDialogs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';

import {
  getCourseEnrollmentOptions,
  getCourseEnrollmentQueryKey,
  updateEnrollmentCompletionDateMutation,
} from '@drivingdoc/api-client/generated/react-query';

import DateInput from 'components/FormInputs/DateInput';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { getZodObjectDefaults } from 'utils/getZodObjectDefaults';

import FeedbackDialog from '../../FeedbackDialog';
import { UpdateCourseEnrollmentFormValues, UpdateCourseEnrollmentSchema } from './UpdateCourseEnrollmentSchema';

interface UpdateCourseEnrollmentDialogPayload {
  id: string;
}

function UpdateCourseEnrollmentDialog({ payload, open, onClose }: DialogProps<UpdateCourseEnrollmentDialogPayload>) {
  const { id } = payload;

  const dialogs = useDialogs();
  const queryClient = useQueryClient();

  const courseEnrollment = useQuery({
    ...getCourseEnrollmentOptions({
      path: { id },
    }),
    gcTime: 0,
    enabled: !!id,
  });

  const courseEnrollmentMutation = useMutation({
    ...updateEnrollmentCompletionDateMutation(),
  });

  const form = useForm<UpdateCourseEnrollmentFormValues>({
    mode: 'all',
    resolver: zodResolver(UpdateCourseEnrollmentSchema),
  });

  const { handleSubmit } = form;

  /** Shared logic for both the update and clear paths. */
  const submitCompletionDate = async (completionDate: string | null) => {
    const result = await courseEnrollmentMutation.mutateAsync({
      path: { id },
      body: { completionDate },
    });

    await queryClient.invalidateQueries({
      queryKey: getCourseEnrollmentQueryKey({ path: { id } }),
    });

    await onClose();

    if (result?.certificate_errors && result.certificate_errors.length > 0) {
      dialogs.open(FeedbackDialog, {
        title: 'Enrollment Updated — Certificate Warning',
        message: `Enrollment updated, but the completion certificate failed to generate: ${result.certificate_errors.join('; ')}`,
      });
    } else {
      dialogs.open(FeedbackDialog, {
        icon: 'success',
        title: 'Course Completion Date Updated',
      });
    }
  };

  const onSubmit = async ({ completionDate }: UpdateCourseEnrollmentFormValues) => {
    try {
      const confirmed = await dialogs.confirm('Are you sure you want to update the course completion date?', {
        okText: 'Confirm',
        cancelText: 'Cancel',
      });
      if (!confirmed) return;

      await submitCompletionDate(completionDate);
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error updating course completion date',
        message: getApiErrorMessage(error),
      });
    }
  };

  const handleClearDate = async () => {
    try {
      const confirmed = await dialogs.confirm(
        'Are you sure you want to clear the completion date? This will revert the enrollment to incomplete.',
        { okText: 'Clear Date', cancelText: 'Cancel' },
      );
      if (!confirmed) return;

      await submitCompletionDate(null);
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error clearing completion date',
        message: getApiErrorMessage(error),
      });
    }
  };

  useEffect(() => {
    if (courseEnrollment.isSuccess && courseEnrollment.data?.completed_at) {
      form.reset({
        completionDate: dayjs(courseEnrollment.data.completed_at).format('YYYY-MM-DD'),
      });
    } else if (courseEnrollment.isSuccess) {
      form.reset(getZodObjectDefaults(UpdateCourseEnrollmentSchema));
    }
  }, [courseEnrollment.isSuccess, courseEnrollment.data, form]);

  return (
    <Dialog fullWidth open={open} onClose={() => onClose()}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...form}>
          <DialogTitle>Update Course Completion Date</DialogTitle>
          <DialogContent>
            <Grid container rowSpacing={3} columnSpacing={1}>
              <Grid size={12}>
                <DateInput name="completionDate" label="Course Completion Date" />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onClose()}>Cancel</Button>
            {courseEnrollment.data?.completed_at && (
              <Button color="error" onClick={handleClearDate} disabled={courseEnrollmentMutation.isPending}>
                Clear Date
              </Button>
            )}
            <Button type="submit" disabled={courseEnrollmentMutation.isPending}>
              Update
            </Button>
          </DialogActions>
        </FormProvider>
      </form>
      <Backdrop open={courseEnrollment.isLoading || courseEnrollmentMutation.isPending} sx={{ color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}

export default UpdateCourseEnrollmentDialog;
